import { type ParsedContact, dedupeKey } from "./contact";
import { readCsv, stripBom } from "./csv";
import { GOOGLE_HEADER_GROUPS, googleContacts } from "./formats/google";
import { LINKEDIN_HEADERS, linkedinContacts } from "./formats/linkedin";
import { vcardContacts } from "./formats/vcard";
import { readVCards } from "./vcard";

/**
 * Recognises an address-book export from the file itself and runs it through
 * the right parser (R2 — there is no column mapper, D8). Refuses anything
 * empty, oversized, unrecognised or unparseable (R3) and excludes nameless
 * and duplicate rows from the preview (R4).
 */

export const MAX_BYTES = 20 * 1024 * 1024;
export const MAX_ROWS = 20_000;

/** How far into the file a header row may sit (LinkedIn ships a preamble). */
const HEADER_SEARCH_ROWS = 10;

export type ImportFormat = "linkedin" | "google" | "vcard";

export interface ImportSummary {
	readonly noName: number;
	readonly duplicate: number;
}

export type ImportError =
	| { readonly kind: "empty"; readonly message: string }
	| { readonly kind: "unrecognised"; readonly message: string }
	| { readonly kind: "tooLarge"; readonly message: string }
	| {
			readonly kind: "malformed";
			readonly format: ImportFormat;
			/** 1-based row or card number; never the row's own text. */
			readonly row: number;
			readonly message: string;
	  };

export type ParseOutcome =
	| {
			readonly ok: true;
			readonly format: ImportFormat;
			readonly contacts: ParsedContact[];
			readonly excluded: ImportSummary;
	  }
	| { readonly ok: false; readonly error: ImportError };

const FORMAT_LABELS: Record<ImportFormat, string> = {
	linkedin: "LinkedIn Connections",
	google: "Google Contacts",
	vcard: "vCard",
};

const refuse = (error: ImportError): ParseOutcome => ({ ok: false, error });

const tooLarge = refuse({
	kind: "tooLarge",
	message:
		"That file is too large. Contact imports must be under 20 MB and 20,000 rows.",
});

const malformed = (format: ImportFormat, row: number): ParseOutcome =>
	refuse({
		kind: "malformed",
		format,
		row,
		message: `That ${FORMAT_LABELS[format]} file could not be read at row ${row}.`,
	});

/**
 * Byte length, without copying a file that is already over the ceiling on
 * character count alone (UTF-8 is never shorter than one byte per character).
 */
const byteLength = (text: string): number =>
	text.length > MAX_BYTES ? text.length : new Blob([text]).size;

/** Drops nameless and within-file duplicate rows, counting each (R4). */
const collect = (
	format: ImportFormat,
	parsed: readonly ParsedContact[],
): ParseOutcome => {
	const seen = new Set<string>();
	const contacts: ParsedContact[] = [];
	let noName = 0;
	let duplicate = 0;
	for (const contact of parsed) {
		if (contact.name === "") {
			noName += 1;
			continue;
		}
		const key = dedupeKey(contact);
		if (seen.has(key)) {
			duplicate += 1;
			continue;
		}
		seen.add(key);
		contacts.push(contact);
	}
	return { ok: true, format, contacts, excluded: { noName, duplicate } };
};

const fromVCards = (source: string): ParseOutcome => {
	if ((source.match(/^BEGIN:VCARD/gim) ?? []).length > MAX_ROWS)
		return tooLarge;
	const { cards, unterminatedLine } = readVCards(source);
	if (unterminatedLine !== undefined)
		return malformed("vcard", unterminatedLine);
	if (cards.length === 0) return malformed("vcard", 1);
	return collect("vcard", vcardContacts(cards));
};

const fromTable = (
	format: ImportFormat,
	rows: readonly string[][],
	headerIndex: number,
	unterminatedRow: number | undefined,
): ParseOutcome => {
	if (unterminatedRow !== undefined && unterminatedRow > headerIndex)
		return malformed(format, unterminatedRow - headerIndex - 1);
	const header = rows[headerIndex] ?? [];
	const body = rows
		.slice(headerIndex + 1)
		.filter((row) => row.some((field) => field.trim() !== ""));
	// More fields than the header means quoting broke somewhere on that row.
	const wide = body.findIndex((row) => row.length > header.length);
	if (wide !== -1) return malformed(format, wide + 1);
	return collect(
		format,
		format === "linkedin"
			? linkedinContacts(header, body)
			: googleContacts(header, body),
	);
};

export const parseContactFile = (text: string): ParseOutcome => {
	if (byteLength(text) > MAX_BYTES) return tooLarge;

	const source = stripBom(text);
	if (source.trim() === "")
		return refuse({ kind: "empty", message: "That file is empty." });

	if (/^\s*BEGIN:VCARD/i.test(source)) return fromVCards(source);

	const { rows, unterminatedRow } = readCsv(source, MAX_ROWS + 1);
	if (rows.length > MAX_ROWS) return tooLarge;

	const has = (names: readonly string[]) => (row: readonly string[]) =>
		names.every((name) => row.includes(name));
	/** Each group is a set of alternative spellings; one match per group. */
	const hasEach =
		(groups: readonly (readonly string[])[]) => (row: readonly string[]) =>
			groups.every((group) => group.some((name) => row.includes(name)));
	const head = rows.slice(0, HEADER_SEARCH_ROWS);

	const linkedinAt = head.findIndex(has(LINKEDIN_HEADERS));
	if (linkedinAt !== -1)
		return fromTable("linkedin", rows, linkedinAt, unterminatedRow);

	const googleAt = head.findIndex(hasEach(GOOGLE_HEADER_GROUPS));
	if (googleAt !== -1)
		return fromTable("google", rows, googleAt, unterminatedRow);

	return refuse({
		kind: "unrecognised",
		message:
			"That file is not a LinkedIn Connections, Google Contacts or vCard export.",
	});
};
